import API from './API';

// Join usati da tutte le letture del dispatcher. NOTA: niente join
// 'driver'/'jolly_driver' a livello journey — @nestjsx/crud assegna ai join
// annidati l'alias dell'ultimo segmento, quindi 'routes.driver' e 'driver'
// collidono ("Not unique table/alias"). La board lavora solo per tratta.
const DISPATCHER_JOIN = [
  'partner',
  'routes',
  'routes.startPoint',
  'routes.endPoint',
  'routes.driver',
  'routes.jolly_driver',
  // Passeggeri delle tratte a catena (driver che rientrano a bordo).
  'routes.passengers',
  'routes.passengers.driver',
];

// Spostamenti Interni (journey): endpoint di preventivo (EstimatePage) +
// endpoint della board dispatcher (Movimenti).
export default {
  // --- Preventivo (EstimatePage) ---
  // Distanza/tempo di una tratta (Google, lato server): { km, min, source }
  legDistance(startPoint, endPoint) {
    return API.post('/journey/leg-distance', { startPoint, endPoint });
  },

  // Prezzo da fasce tariffarie: { price, price_per_km, band_id, scope }
  // (price null se nessuna fascia, del partner o globale, copre i km).
  quote(partnerId, km, date) {
    return API.post('/tariff-band/quote', { partnerId, km, date });
  },

  // Tariffa di ogni corsa dello spostamento che si sta inserendo (solo
  // Simba): `corse` = [{ km, two_operators, immatricolato }], risponde
  // { corse: [{ price, price_per_km, scope }] } nello stesso ordine.
  // Silenziosa: lo stato lo mostra la corsa.
  tariffaCorse(partnerId, date, corse) {
    return API.post('/journey/tariffa', { partner_id: partnerId, date, corse }, { skipErrorToast: true });
  },

  // --- Creazione percorso (AddJourneyPage) ---
  //
  // Stesse rotte usate dal gestionale partner: la logica di creazione è
  // identica, cambia solo chi sceglie il partner (lì è implicito, qui no).
  //
  // ⚠️ `POST /journey` valida con `whitelist + forbidNonWhitelisted`: ogni
  // campo non dichiarato in `CreateJourneyDto` fa fallire la richiesta con
  // 400. Il payload va costruito da `buildJourneyPayload`, non a mano.
  addJourney(journey) {
    return API.post('/journey', journey);
  },

  // Distanza e durata di una tratta. Alias di `legDistance` col nome usato
  // dal composable, che è condiviso col gestionale partner.
  // Silenziosa: se l'indirizzo non è riconosciuto il motivo si mostra sulla
  // corsa (`leg.previewError`), non in un toast col codice del provider.
  previewLeg(startPoint, endPoint) {
    return API.post('/journey/leg-distance', { startPoint, endPoint }, { skipErrorToast: true });
  },

  // Disponibilità delle corse del modulo: per ognuna { available, reason,
  // first_available_start }. `journeyId` in modifica esclude le corse già
  // salvate. Silenziosa: lo stato lo mostra la corsa.
  checkAvailability(legs, journeyId) {
    return API.post('/journey/availability-check', { legs, journey_id: journeyId }, { skipErrorToast: true });
  },

  // Geolocalizza un indirizzo scritto a mano e lo restituisce strutturato.
  geocodePoint(point) {
    return API.post('/journey/geocode-point', { point });
  },

  // Percorsi già presenti in una giornata PER UN PARTNER. Il filtro sul
  // partner qui è indispensabile: nel gestionale admin la lista non è
  // ristretta a un tenant. (Non c'è più un tetto giornaliero: `num_driver` non
  // limita niente, la disponibilità si verifica corsa per corsa.)
  getJourneysByDay(fromISO, toISO, partnerId) {
    const filtri = [
      `filter=date||$gte||${fromISO}`,
      `filter=date||$lt||${toISO}`,
      'filter=status||$ne||ELIMINATA',
      `filter=partnerId||$eq||${partnerId}`,
    ].join('&');
    return API.get(`/journey?${filtri}`, { params: { limit: 200, page: 1 } });
  },

  // --- Board dispatcher (Movimenti) ---
  // Journey di tutti i partner; l'intervallo [startDate, endDate) e il partner
  // sono opzionali (assenti -> lista generale completa).
  // `partnerIds`: i partner di una provincia. Lo spostamento interno
  // non ha una sede, quindi la provincia si riconosce dal partner.
  getJourneysByRange(startDate, endDate, partnerId, partnerIds = null) {
    let url = '/journey?sort=date,ASC';
    if (startDate && endDate) {
      url += `&filter=date||$gte||${startDate}&filter=date||$lt||${endDate}`;
    }
    if (partnerId) {
      url += `&filter=partner.id||$eq||${partnerId}`;
    }
    if (partnerIds && partnerIds.length) {
      url += `&filter=partner.id||$in||${partnerIds.join(',')}`;
    }
    return API.get(url, {
      params: {
        join: DISPATCHER_JOIN,
      },
    });
  },

  // Quanti spostamenti conclusi ci sono IN TUTTO. Come per i booking si
  // chiede una riga sola e si legge `total`. Conta le GIORNATE di spostamento
  // (`journey`), non le singole tratte: un percorso di tre corse è 1.
  countConclusi({ partnerId = '', partnerIds = null } = {}) {
    const and = [{ status: { $eq: 'CONCLUSA' } }];
    if (partnerId) and.push({ 'partner.id': { $eq: partnerId } });
    else if (partnerIds) and.push({ 'partner.id': { $in: partnerIds } });
    return API.get('/journey', {
      params: { page: 1, limit: 1, s: JSON.stringify({ $and: and }) },
    }).then((r) => r.data?.total ?? 0);
  },

  getJourneyById(id) {
    return API.get(`/journey/${id}`, {
      params: {
        // Nel dettaglio serve anche CHI l'ha creato e chi l'ha modificato per
        // ultimo: il backend li scrive dal token di chi salva. Non nelle
        // liste, dove sarebbero due join in più per riga.
        join: [...DISPATCHER_JOIN, 'employee', 'modified_by_employee'],
      },
    });
  },

  // Chi ha INSERITO ogni tratta (`route.employee`), come mappa
  // { [routeId]: employee }.
  //
  // ⚠️ Chiamata a parte di proposito: `routes.employee` e `employee` generano
  // lo stesso alias `employee` e insieme il server risponde «Not unique
  // table/alias» (stesso inciampo di driver e jolly). Qui si chiede solo il
  // ramo delle tratte, nel dettaglio solo quello dello spostamento.
  getRouteAuthors(journeyId) {
    return API.get(`/journey/${journeyId}`, {
      params: { join: ['routes', 'routes.employee'] },
      skipErrorToast: true,
    }).then((r) => Object.fromEntries(
      (r.data?.routes || []).map((route) => [route.id, route.employee || null]),
    ));
  },

  // Assegnazione dispatcher per tratta:
  // { assignments: [{ routeId, driverId|null, jollyDriverId|null,
  //                   extraMinDriver, extraMinJolly }], status? }
  //
  // I minuti aggiuntivi sono attese e soste fuori dagli orari della tratta: il
  // COD6 li paga come tempo (minuti ÷ 60 × tariffa oraria dell'agenzia).
  assignDrivers(journeyId, payload) {
    return API.patch(`/journey/${journeyId}/assignment`, payload);
  },

  // Modifica della SINGOLA corsa (tipo, orari, sedi, veicolo): driver,
  // minuti, rimborsi e foto restano. Risponde con lo spostamento e i
  // `conflitti` d'agenda.
  // Modifica COMPLETA dello spostamento (corse in mezzo, più auto, rientri).
  // Da Simba le corse col loro `id` restano le stesse: driver, foto, minuti
  // e rimborsi non si perdono.
  updateJourney(journeyId, payload) {
    return API.patch(`/journey/${journeyId}`, payload);
  },

  updateRoute(routeId, payload) {
    return API.patch(`/journey/route/${routeId}`, payload);
  },

  // Solo le note della corsa: non cambia stato, prezzo né «ultime modifiche».
  setRouteNote(routeId, details) {
    return API.patch(`/journey/route/${routeId}/note`, { details });
  },

  // Stato della singola corsa (conferma) -> journey con stato-giornata ricalcolato.
  setRouteStatus(routeId, status) {
    return API.patch(`/journey/route/${routeId}/status`, { status });
  },

  // Avanzamento della corsa (`route.execution_status`): normalmente lo muove
  // l'app del driver, un passo alla volta e con le foto. Simba lo corregge da
  // qui in qualsiasi direzione — una corsa fatta senza aprire l'app
  // resterebbe altrimenti ferma per sempre.
  setRouteExecution(routeId, executionStatus) {
    return API.patch(`/journey/route/${routeId}/execution`, { executionStatus }, { skipErrorToast: true });
  },

  // Set fotografici della tratta, uno per fase (PARTENZA / ARRIVO), con
  // `urls: { url1..url4 }` pre-firmati a scadenza.
  getRoutePhotos(routeId) {
    return API.get(`/journey/route/${routeId}/photos`, { skipErrorToast: true }).then((r) => r.data || []);
  },

  // Allegati liberi della tratta (DDT, moduli firmati), ognuno con `url`.
  getRouteAttachments(routeId) {
    return API.get(`/journey/route/${routeId}/attachments`, { skipErrorToast: true }).then((r) => r.data || []);
  },

  // Stesso contratto degli allegati della prenotazione.
  uploadRouteAttachment(routeId, { file, fileName }) {
    return API.post(`/journey/route/${routeId}/attachments`, {
      file,
      file_name: String(fileName || '').slice(0, 255) || undefined,
    });
  },

  deleteRouteAttachment(routeId, attachmentId) {
    return API.delete(`/journey/route/${routeId}/attachments/${attachmentId}`);
  },

};
